import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export const API = axios.create({
  baseURL: BASE_URL,
});

// Request interceptor — attach access token (no more X-User-ID)
API.interceptors.request.use((config) => {
  try {
    const authData = JSON.parse(localStorage.getItem('sdny_auth_v1') || '{}');
    if (authData?.token) {
      config.headers['Authorization'] = `Bearer ${authData.token}`;
    }
  } catch {}
  return config;
});

// Response interceptor — auto-refresh on 401
let isRefreshing = false;
let pendingRequests = [];

const processQueue = (error, token = null) => {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingRequests = [];
};

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip refresh for login/register/refresh endpoints
    const skipPaths = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'];
    if (skipPaths.some((p) => originalRequest.url?.includes(p))) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return API(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const authData = JSON.parse(localStorage.getItem('sdny_auth_v1') || '{}');
        if (!authData?.refresh_token) {
          throw new Error('No refresh token');
        }

        const { data } = await axios.post(
          `${BASE_URL}/auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${authData.refresh_token}` } }
        );

        // Update stored tokens
        authData.token = data.access_token || data.token;
        localStorage.setItem('sdny_auth_v1', JSON.stringify(authData));

        processQueue(null, authData.token);
        originalRequest.headers['Authorization'] = `Bearer ${authData.token}`;
        return API(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Refresh failed — clear auth and redirect to login
        localStorage.removeItem('sdny_auth_v1');
        window.dispatchEvent(new Event('sdny_auth_expired'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const ENDPOINTS = {
  cart: '/cart',
  cartItem: (id) => `/cart/${id}`,
  auth: {
    login: '/auth/login',
    register: '/auth/register',
  },
  orders: '/orders',
  order: (id) => `/orders/${id}`,
};
